import React from 'react';
import { useResizable } from 'react-resizable-layout';

import { rolloutService } from '@/core/services/RolloutService';
import { TargetType } from '@/server/services/GoogleBigQuery/BigQueryIncidentsClient.types';
import { RightDrawer } from '../../../../common/RightDrawer';
import { ActiveTarget, useMapEventsContext } from '../../context/MapEventsContextProvider';
import { TargetAssetCard } from '@/components/composite/GeoMap/cards/TargetAssetCard';
import { TargetIncidentCard } from '@/components/composite/GeoMap/cards/TargetIncidentCard';
import { SupplyChainMappingUrlLink } from '@/components/composite/GeoMap/cards/SupplyChainMappingUrlLink';
import { CountryFlag } from '@/components/common/CountryFlag';

const DRAWER_MIN_WIDTH = 360;
const DRAWER_MAX_WIDTH = 1000;
const DRAWER_DEFAULT_WIDTH = 420;

const targetIs = {
  Asset: (target: ActiveTarget | null): target is React.ComponentProps<typeof TargetAssetCard>['target'] =>
    target?._targetType === TargetType.Asset,
  Incident: (target: ActiveTarget | null): target is React.ComponentProps<typeof TargetIncidentCard>['target'] =>
    target?._targetType === TargetType.Incident,
};

export const RightSidebarDrawer = () => {
  const { activeTarget, setActiveTarget } = useMapEventsContext();

  const { position, separatorProps, isDragging } = useResizable({
    axis: 'x',
    initial: DRAWER_DEFAULT_WIDTH,
    min: DRAWER_MIN_WIDTH,
    max: DRAWER_MAX_WIDTH,
    reverse: true,
  });

  const resizeProps = rolloutService.isAsidePanelsResizeEnabled()
    ? { width: position, resizeSeparatorProps: separatorProps, isResizing: isDragging }
    : {};

  if (targetIs.Asset(activeTarget)) {
    return (
      <RightDrawer
        {...resizeProps}
        headerTitle={
          <div className='flex items-center gap-2 justify-between'>
            <span>{activeTarget.AssetName}</span>
            <CountryFlag countryName={activeTarget.AssetName} />
          </div>
        }
        contentBody={<TargetAssetCard
          target={activeTarget}
          actions={<SupplyChainMappingUrlLink value={activeTarget.SupplyChainMapping} className="mt-4" />}
        />}
        onClose={() => setActiveTarget(null)}
      />
    );
  }

  if (targetIs.Incident(activeTarget)) {
    return (
      <RightDrawer
        {...resizeProps}
        headerTitle={<div>{activeTarget.Title}</div>}
        contentBody={<TargetIncidentCard
          target={activeTarget}
          actions={<SupplyChainMappingUrlLink value={activeTarget.SupplyChainMapping} className="px-5" />}
        />}
        onClose={() => setActiveTarget(null)}
      />
    );
  }

  return null;
};
